import React, { useState, useEffect } from 'react';
// import { useIsFetching } from '@tanstack/react-query';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import Button from '../../Button';
import NoData from '../../NoData';
import BankAccountForm from './BankAccountForm';
import AddOrEditPageTabs from '../AddOrEditPageTabs';

import useBankAccountForm from '../../../forms/loan/bankAccount';
import { useUiContext } from '../../../context/UiContext';
import BankAccountService from '../../../services/BankAccountService';
import BorrowerService from '../../../services/BorrowerService';
import LoanService from '../../../services/LoanService';
import LenderService from '../../../services/LenderService';
import { useServicerStore } from '../../../store';

const defaultTtabs = [
  {
    label: 'ACH Settings',
    displayLabel: 'ACH Schedule',
  },
  {
    label: 'Bank Account',
  },
];

const lenderTabs = [
  {
    label: 'Bank Account',
  },
];

export default function BankAccount({
  loan,
  loanId,
  lenderId,
  lender,
  isFetching,
  refetch,
  vendor,
  isLenderPage,
}) {
  const [bankAccounts, setBankAccounts] = useState([]);

  const { showToast } = useUiContext();

  const { t } = useTranslation();

  const userRole = useServicerStore((state) => state.userRole);
  const isBorrower = userRole === 'borrower';
  const isLenderUserRole = userRole === 'lender';

  const primaryBorrower = loan?.borrowers?.find(
    (borrower) => borrower.position === 'primary'
  );

  const tabs = isLenderPage ? lenderTabs : defaultTtabs;

  // If user is a lender, bank accounts are fetched from the lender/{lender_id}/bank_account endpoint
  const { data: lenderBankAccounts } = useQuery({
    queryKey: ['lenderBankAccounts', lenderId],
    queryFn: () => LenderService.getLenderBankAccounts(lenderId),
    enabled: !!lenderId && isLenderUserRole,
  });

  const {
    data: borrower,
    refetch: refetchBorrower,
    isBorrowerFetching,
  } = useQuery({
    queryKey: ['borrower', primaryBorrower?.borrower_id],
    queryFn: () => BorrowerService.getBorrower(primaryBorrower.borrower_id),
    enabled: !!primaryBorrower,
  });

  const refetchAll = () => {
    refetch();
    refetchBorrower();
  };

  useEffect(() => {
    if (borrower?._bank_accounts?.length) {
      setBankAccounts(borrower._bank_accounts);
    }
    if (lender?._bank_account) {
      setBankAccounts([lender._bank_account]);
    }
    if (vendor?._bank_account) {
      setBankAccounts([vendor._bank_account]);
    }
  }, [borrower, lender?._bank_account, vendor?._bank_account]);

  // If user is a lender, bank accounts are fetched from the lender/{lender_id}/bank_account endpoint
  useEffect(() => {
    if (isLenderUserRole && lenderBankAccounts?.length) {
      setBankAccounts(
        lenderBankAccounts.sort((a, b) => {
          if (a.status === 'active' && b.status === 'active') {
            return 0;
          }

          if (a.status === 'active') {
            return -1;
          }

          if (b.status === 'active') {
            return 1;
          }

          return 0;
        })
      );
    }
  }, [isLenderUserRole, lenderBankAccounts]);

  const {
    mutate: createBorrowerBankAccount,
    isPending: isBorrowerBankAccountPending,
  } = useMutation({
    mutationFn: async (formValues) => {
      const bankAccount = await BankAccountService.createBankAccount({
        ...formValues,
        object_id: primaryBorrower.borrower_id,
        object_type: 'Borrower',
        function: 'payment',
      });

      if (!loan?.bank_account_id) {
        await LoanService.updateLoan({
          loanId,
          formValues: {
            ...loan,
            bank_account_id: bankAccount.id,
          },
        });

        await refetch();
      }
    },
    onSuccess: () => {
      showToast({
        text: 'Bank account created successfully',
        type: 'success',
      });
      refetchBorrower();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const {
    mutate: createLenderOrVendorBankAccount,
    isPending: isLenderOrVendorBankAccountPending,
  } = useMutation({
    mutationFn: async (formValues) => {
      const isVendor = !!vendor;

      const values = {
        ...formValues,
        object_id: isVendor ? vendor.id : lender.id,
        object_type: isVendor ? 'Vendor' : 'Lender',
      };

      delete values.id;
      delete values.notSaved;

      if (isVendor) {
        values.function = 'payment';
      }

      const bankAccount = await BankAccountService.createBankAccount({
        ...values,
      });

      const previousBankAccount = isVendor
        ? vendor._bank_account
        : lender._bank_account;

      // Archive the previous bank account if it exists
      if (previousBankAccount) {
        await BankAccountService.archiveBankAccount(previousBankAccount.id);
      }

      // Set the new bank account to the lender or Vendor
      if (!isVendor) {
        await LenderService.updateLender({
          lenderId,
          formValues: {
            ...lender,
            _bank_account: bankAccount,
          },
        });
      }

      return refetch();
    },
    onSuccess: () => {
      showToast({
        text: 'Bank account created successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: useBankAccount, isPending: isUseBankAccountPending } =
    useMutation({
      mutationFn: (bankAccountId) =>
        LoanService.updateLoan({
          loanId,
          formValues: {
            ...loan,
            bank_account_id: bankAccountId,
          },
        }),
      onSuccess: () => {
        showToast({
          text: 'Bank account updated successfully',
          type: 'success',
        });
        refetch();
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const { mutate: deleteLoanBankAccount, isPending: isDeleting } = useMutation({
    mutationFn: (bankAccountId) =>
      BankAccountService.deleteBankAccount(bankAccountId),
    onSuccess: async () => {
      await refetchBorrower();
      showToast({
        text: 'Bank account deleted successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const isBankAccountPending =
    isBorrowerBankAccountPending ||
    isLenderOrVendorBankAccountPending ||
    isDeleting;

  // This is used to get all the initial values from the form so we can add them to the empty bank account
  // Otherwise, validation will not work
  const emptyForm = useBankAccountForm(() => {}, null, !!lenderId);

  const addBankAccount = () => {
    setBankAccounts((prevBankAccounts) => [
      { id: Math.random(), notSaved: true, ...emptyForm.formik.initialValues },
      ...prevBankAccounts,
    ]);
  };

  const isBankAccountFormVisible = bankAccounts.some(
    (bankAccount) => bankAccount.notSaved
  );

  const isLender = !!lenderId;

  if (!loan?.borrowers?.length && !lenderId && !vendor) {
    return (
      <div className="flex items-center justify-center h-full">
        {isFetching ? '' : 'No borrowers added yet'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex gap-4 flex-col md:flex-row md:justify-between md:items-center">
        {!isBorrower && !isLenderUserRole && !vendor && !lender && (
          <AddOrEditPageTabs tabs={tabs} />
        )}
        {!isLenderUserRole && bankAccounts?.length ? (
          <Button
            className={`${
              isLender ? 'md:!max-w-[220px]' : 'md:!max-w-[200px]'
            } ml-auto`}
            onClick={addBankAccount}
            style={{
              visibility: isBankAccountFormVisible ? 'hidden' : 'visible',
            }}
          >
            <svg
              className="w-4 h-4 fill-current opacity-50 shrink-0"
              viewBox="0 0 16 16"
            >
              <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
            </svg>
            <span className="ml-2 whitespace-nowrap">
              {(isLender && lender?._bank_account) || vendor?._bank_account
                ? t(
                    'AddOrEditPage.AddOrEditLoan.Sections.BankAccount.Replace Bank Account'
                  )
                : t(
                    'AddOrEditPage.AddOrEditLoan.Sections.BankAccount.Add Bank Account'
                  )}
            </span>
          </Button>
        ) : null}
      </div>
      <div className="flex-grow flex flex-col gap-10 overflow-auto">
        {bankAccounts.map((bankAccount) => (
          <BankAccountForm
            key={bankAccount.id}
            bankAccount={bankAccount}
            setBankAccounts={setBankAccounts}
            createBankAccount={
              isLender || !!vendor
                ? createLenderOrVendorBankAccount
                : createBorrowerBankAccount
            }
            isPending={isBankAccountPending}
            loanBankAccountId={loan?.bank_account_id}
            useBankAccount={useBankAccount}
            isUseBankAccountPending={isUseBankAccountPending}
            isFetching={isFetching || isBorrowerFetching}
            isLender={!!lenderId}
            deleteLoanBankAccount={deleteLoanBankAccount}
            isLenderUserRole={isLenderUserRole}
            refetch={refetchAll}
            isVendor={!!vendor}
          />
        ))}
        {!bankAccounts.length ? (
          <NoData
            buttonLabel={t(
              'AddOrEditPage.AddOrEditLoan.Sections.BankAccount.Add Bank Account'
            )}
            title={t(
              vendor
                ? 'This vendor does not have a bank account. Any payments to this vendor will be made with a check.'
                : 'AddOrEditPage.AddOrEditLoan.Sections.BankAccount.No bank accounts have been added yet'
            )}
            noSubtitle
            onButtonClick={addBankAccount}
          />
        ) : null}
      </div>
    </div>
  );
}
